// Sprint 65 — Deal Baazar pricing helpers (display-only).
//
// Reuses existing product objects. We NEVER mutate product docs; instead we
// return a shallow copy with `selling_price` overridden to the deal price and
// `mrp` set to the ORIGINAL selling_price (so the discount % on ProductCard
// and DealProductCard renders correctly). The deal price is scoped strictly
// to Deal Baazar routes — callers outside `/deal-baazar` must never call
// these helpers.

const num = (v) => Number(v || 0);

/** Get the effective deal price for a product inside a campaign. */
export function dealPriceFor(product, campaign, override) {
  const original = num(product?.selling_price);
  if (!original) return 0;

  // Per-line override wins first (from campaign.products[].deal_price)
  if (override && (override.deal_price || override.discount_value)) {
    if (override.deal_price)         return Math.max(0, Math.round(override.deal_price));
    if (override.discount_type === "flat") return Math.max(0, Math.round(original - num(override.discount_value)));
    if (override.discount_type === "pct")  return Math.max(0, Math.round(original * (1 - num(override.discount_value) / 100)));
  }

  // Campaign-level bulk discount
  if (campaign?.flat_discount)       return Math.max(0, Math.round(original - num(campaign.flat_discount)));
  if (campaign?.pct_discount)        return Math.max(0, Math.round(original * (1 - num(campaign.pct_discount) / 100)));

  return original;
}

/** Clone a product with the deal price baked into selling_price + original as mrp. */
export function applyDealPrice(product, campaign, override) {
  const original = num(product?.selling_price);
  const originalMrp = num(product?.mrp) || original;
  const deal = dealPriceFor(product, campaign, override);
  if (deal >= original) return product;                     // no discount
  return {
    ...product,
    selling_price: deal,
    mrp: Math.max(originalMrp, original),                   // strike-through anchor
    _dealTag: override?.tag || null,
    _dealSavings: Math.round(original - deal),
  };
}

/** Filter campaigns to only those live NOW and active. */
export function activeCampaigns(campaigns) {
  const now = Date.now();
  return (campaigns || [])
    .filter((c) => c && c.is_active !== false)
    .filter((c) => {
      const s = c.start_date ? Date.parse(c.start_date) : null;
      const e = c.end_date   ? Date.parse(c.end_date)   : null;
      if (s && s > now) return false;
      if (e && e < now) return false;
      return true;
    })
    .sort((a, b) => (b.priority || 0) - (a.priority || 0));
}

/** ms remaining until end. null = no end date. */
export function msRemaining(campaign) {
  if (!campaign?.end_date) return null;
  const t = Date.parse(campaign.end_date);
  if (Number.isNaN(t)) return null;
  return Math.max(0, t - Date.now());
}

/** Format a duration (ms) into a HH:MM:SS or Nd HH:MM:SS string. */
export function fmtRemaining(ms) {
  if (ms == null) return "";
  if (ms <= 0)    return "Ended";
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return d > 0 ? `${d}d ${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

/** Well-known campaign template presets. */
export const DEAL_TEMPLATES = [
  { key: "flash_sale",       label: "Flash Sale",         emoji: "🔥", theme: "#DC1E1E", show_timer: true  },
  { key: "near_expiry",      label: "Near Expiry Deals",  emoji: "⏳", theme: "#E85400", show_timer: false },
  { key: "festival",         label: "Festival Deals",     emoji: "🎉", theme: "#C0392B", show_timer: true  },
  { key: "clearance",        label: "Clearance Sale",     emoji: "📦", theme: "#8B1E2E", show_timer: false },
  { key: "weekend",          label: "Weekend Deals",      emoji: "⚡", theme: "#D6336C", show_timer: true  },
  { key: "mega_deal",        label: "Mega Deal",          emoji: "💥", theme: "#B31212", show_timer: true  },
  { key: "buy_more_save",    label: "Buy More Save More", emoji: "🏷",  theme: "#A52A2A", show_timer: false },
];

/** Slugify a campaign name for URL. */
export const slugify = (s = "") =>
  String(s).toLowerCase().trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
